import { slice } from 'lodash';
import * as XLSX from 'xlsx';
import { extractErrorMessage } from "@common/utils/errorUtils";

interface TableColumn {
    key: string;
    label: string;
    width?: string;
    type?: 'text' | 'date' | 'checkbox' | 'boolean' | 'currency' | 'percentage';
    currency?: string;
    format?: string;
}

/**
 * Reusable 3-Level Nested Table Component with Pagination Support
 *
 * This component is dedicated to displaying 3-level nested tables:
 * - Level 1: Main rows (e.g., clients)
 * - Level 2: Expanded content rows (e.g., date groupings)
 * - Level 3: Nested table within level 2 rows (e.g., portfolio data)
 *
 * Usage example:
 * <reusable-nested-table
 *   columns="$ctrl.tableColumns"
 *   data="$ctrl.tableData"
 *   enable-pagination="true"
 *   title="My Table"
 *   on-page-change="$ctrl.onPageChanged()">
 * </reusable-nested-table>
 *
 * Data structure expected:
 * - Level 1 items have `expandedContent` array
 * - Level 2 items have `nestedContent` array and `nestedColumns` array
 * - Level 3 items are displayed in a nested table
 *
 * Pagination features:
 * - Default: 5 items per page (configurable: 5, 10, 25, 50)
 * - Pagination automatically enabled only when data exceeds items per page
 * - Can be manually disabled by setting enable-pagination="false"
 * - on-page-change callback for external handling if needed
 */


class ReusableNestedTableController {
    public columns: TableColumn[];

    public displayColumns: boolean = true;
    public nestedColumns: TableColumn[];
    public displayNestedColumnsHeader: boolean = true;
    public data: any[];
    public emptyMessage: string = 'No items found.';
    public loading: boolean = false;
    public title: string;
    public addButtonText: string = 'Add';
    public addButtonIcon: string = 'add';
    public readonly: boolean = false;
    public itemIdField: string = 'id';
    public selectedItems: Set<any> = new Set();
    public hideExcelButton: boolean = false;
    public autoSizeColumns: boolean = false;
    public showDownloadColumn: boolean = true;

    // Expandable row properties (always enabled for 3-level structure)
    public expandedRows: Set<any> = new Set();
    public alternateExpandedRowFontColors: boolean = false; // Alternate blue/green font colors for expanded rows
    public nestedExpandedRows: Map<any, Set<any>> = new Map(); // Track nested expansion state

    // Dynamic data loading properties
    public cacheExpandedData: boolean = true; // Cache fetched level 2 data
    public loadingRows: Map<any, boolean> = new Map(); // Track loading state per row
    public cachedExpandedData: Map<any, any[]> = new Map(); // Cache fetched level 2 data

    // Pagination properties
    public paginationOptions: any = {
        currentPage: 1,
        itemsPerPage: 5,
        totalItems: 0
    };
    public paginatedData: any[] = [];
    public enablePagination: boolean = false;

    // Level 2 pagination properties (per item)
    public level2PaginationOptions: Map<any, any> = new Map();
    public level2PaginatedData: Map<any, any[]> = new Map();
    public level2ItemsPerPage: number = 5;
    public enableLevel2Pagination: boolean = true;

    public onAdd: () => void;
    public onEdit: (params: { item: any }) => void;
    public onDelete: (params: { itemIds: any[] }) => void | angular.IPromise<void>;
    public onPageChange: () => void;
    public onRefresh: () => void;
    public onRowClick: (params: { item: any }) => void;
    public onLevel1Expand: (params: { item: any }) => Promise<any[]> | any[];
    public onLevel1Download: (params: { item: any }) => void;
    public onLevel2Download: (params: { item: any, subItem: any }) => void;

    private notifyjs: any;

    constructor(notifyjs: any) {
        this.notifyjs = notifyjs;
    }

    public $onInit(): void {
        this.updatePagination();
    }

    public $onChanges(changes: any): void {
        if (changes.data && changes.data.currentValue) {
            this.updatePagination();
        }
        if (changes.level2ItemsPerPage && changes.level2ItemsPerPage.currentValue) {
            this.level2ItemsPerPage = changes.level2ItemsPerPage.currentValue;
            // Reset pagination for all items when items per page changes
            this.level2PaginationOptions.clear();
            this.level2PaginatedData.clear();
        }
        if (changes.enableLevel2Pagination) {
            this.enableLevel2Pagination = changes.enableLevel2Pagination.currentValue !== false;
        }
    }

    public getColumnClass(column: TableColumn): string {
        if (column.width) {
            return column.width;
        }
        return '';
    }

    public getColumnStyle(column: TableColumn): string {
        // If auto-sizing is enabled, don't set explicit widths
        if (this.autoSizeColumns) {
            return '';
        }

        if (column.width) {
            // If width is a CSS value (like '200px', '15%'), use it directly
            if (column.width.includes('px') || column.width.includes('%') || column.width.includes('em')) {
                return `width: ${column.width};`;
            }
            // If it's a Bootstrap class, we'll ignore it for table cells
            return '';
        }
        // Calculate equal width for columns without explicit width
        const totalColumns = this.columns ? this.columns.length : 1;
        const columnsWithoutWidth = this.columns ? this.columns.filter(c => !c.width).length : 1;
        if (columnsWithoutWidth > 0) {
            const equalWidthPercent = (100 / totalColumns);
            return `width: ${equalWidthPercent}%;`;
        }
        return '';
    }

    public getExpandedRowColumnClass(column: TableColumn, index: number): string {
        const baseClass = this.getColumnClass(column);
        const classes: string[] = [];

        if (baseClass) {
            classes.push(baseClass);
        }

        if (index === 0) {
            classes.push('expanded-row-first-column');
        }

        if (column.type === 'currency') {
            classes.push('currency-cell');
        }

        return classes.join(' ');
    }

    /** DD/MM/YYYY for date columns (not browser default, which is often MM/DD/YYYY). */
    private formatDateCell(value: any): string {
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            return value != null ? String(value) : '---';
        }
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    public formatValue(item: any, column: TableColumn): string {
        const value = item[column.key];

        if (value === null || value === undefined) {
            return '---';
        }

        if (column.type === 'date' && value) {
            return this.formatDateCell(value);
        }

        if (column.type === 'boolean') {
            return value ? 'true' : 'false';
        }

        if (column.type === 'currency') {
            const formattedValue = Number(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return column.currency ? `${column.currency} ${formattedValue}` : formattedValue;
        }

        if (column.type === 'percentage') {
            const formattedValue = Number(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

            // Remove negative sign if value is -0.00
            const cleanedValue = formattedValue === '-0.00' ? '0.00' : formattedValue;
            return `${cleanedValue} %`;
        }

        return value.toString();
    }

  public hasData(): boolean {
      return this.paginatedData && this.paginatedData.length > 0;
  }

  public getRowCount(): number {
      return this.data ? this.data.length : 0;
  }

  public updatePagination(): void {
      if (!this.data) {
          this.paginatedData = [];
          this.paginationOptions.totalItems = 0;
          this.enablePagination = false;
          return;
      }

      this.paginationOptions.totalItems = this.data.length;

      // Enable pagination only if data exceeds max items per page
      this.enablePagination = this.data.length > this.paginationOptions.itemsPerPage;

      if (this.enablePagination) {
          const startIndex = (this.paginationOptions.currentPage - 1) * this.paginationOptions.itemsPerPage;
          const endIndex = startIndex + this.paginationOptions.itemsPerPage;
          this.paginatedData = this.data.slice(startIndex, endIndex);
      } else {
          this.paginatedData = this.data;
      }
  }

  public onPageChanged(): void {
      this.updatePagination();
      if (this.onPageChange) {
          this.onPageChange();
      }
  }

  public onItemsPerPageChanged(): void {
      this.paginationOptions.currentPage = 1;
      this.updatePagination();
      if (this.onPageChange) {
          this.onPageChange();
      }
  }

  public toggleSelection(item: any): void {
      if (this.selectedItems.has(item)) {
          this.selectedItems.delete(item);
      } else {
          this.selectedItems.add(item);
      }
  }

  public isSelected(item: any): boolean {
      return this.selectedItems.has(item);
  }

  public getSelectedCount(): number {
      return this.selectedItems.size;
  }

  public hasSelectedItems(): boolean {
      return this.selectedItems.size > 0;
  }

  public selectAll(): void {
      if (this.paginatedData) {
          this.paginatedData.forEach(item => this.selectedItems.add(item));
      }
  }

  public deselectAll(): void {
      this.selectedItems.clear();
  }

  public isAllSelected(): boolean {
      return this.paginatedData && this.paginatedData.length > 0 && this.selectedItems.size === this.paginatedData.length;
  }

  public toggleSelectAll(): void {
      if (this.isAllSelected()) {
          this.deselectAll();
      } else {
          this.selectAll();
      }
  }

  public editItem(item: any): void {
      if (this.onEdit) {
          this.onEdit({item: item});
      }
  }

  public confirmDelete(event?: MouseEvent): void {
      if (event) {
          event.stopPropagation();
          event.preventDefault();
      }

      if (this.hasSelectedItems()) {
          const selectedIds = Array.from(this.selectedItems).map(item => item[this.itemIdField]);
          this.deleteSelectedItems(selectedIds);
      }
  }

  private deleteSelectedItems(itemIds): void {
      const selectedCount = itemIds.length;

      if (!this.onDelete) {
          return;
      }

      const result: void | angular.IPromise<void> = this.onDelete({ itemIds: itemIds });

      // If onDelete returns a Promise, await it: on success show success and clear selection; on failure show error message.
      if (result != null && typeof (result as any).then === 'function') {
          (result as angular.IPromise<void>).then(() => {
              this.selectedItems.clear();
              this.notifyjs(`${selectedCount} item(s) deleted successfully`, "success");
          }).catch((err) => {
              this.notifyjs(extractErrorMessage(err, 'Delete failed.'), "error");
          });
          return;
      }

      // Legacy: synchronous delete – update local data and clear selection
      this.data = this.data.filter(item => !this.selectedItems.has(item));
      this.selectedItems.clear();
  }

  public refreshData(): void {
      if (this.onRefresh) {
          this.onRefresh();
      }
  }

  public rowClick(item: any): void {
      if (this.onRowClick) {
          this.onRowClick({ item: item });
      }
  }

  public downloadExcel(): void {
      if (!this.columns || this.columns.length === 0) {
          this.notifyjs('No columns defined for export', 'warning');
          return;
      }

      if (!this.data || this.data.length === 0) {
          this.notifyjs('No data available to export', 'warning');
          return;
      }

      try {
          // Create workbook and worksheet
          const workbook = XLSX.utils.book_new();

          // Prepare headers from columns
          const headers = this.columns.map(column => column.label);

          // Prepare data rows
          const dataRows = this.data.map(item => {
              return this.columns.map(column => {
                  const value = item[column.key];

                  if (value === null || value === undefined) {
                      return '';
                  }

                  // Format values similar to formatValue method
                  if (column.type === 'date' && value) {
                      return this.formatDateCell(value);
                  }

                  if (column.type === 'boolean') {
                      return value ? 'Yes' : 'No';
                  }

                  return value.toString();
              });
          });

          // Combine headers and data
          const worksheetData = [headers, ...dataRows];

          // Create worksheet
          const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

          // Set column widths
          const columnWidths = this.columns.map(column => {
              let width = 15; // default width
              if (column.width) {
                  // Handle different width formats: 'col-md-2', '2', etc.
                  const widthMatch = column.width.toString().match(/\d+/);
                  if (widthMatch) {
                      width = parseInt(widthMatch[0]) * 2;
                  }
              }
              return { wch: Math.max(width, 10) }; // minimum width of 10
          });
          worksheet['!cols'] = columnWidths;

          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

          // Generate filename with current date (sanitize title)
          const sanitizedTitle = (this.title || 'table_data')
              .replace(/[^a-z0-9]/gi, '_')
              .toLowerCase();
          const fileName = sanitizedTitle + '_' +
                          new Date().toISOString().split('T')[0] + '.xlsx';

          // Write file
          XLSX.writeFile(workbook, fileName);

          this.notifyjs('Excel file downloaded successfully', 'success');
      } catch (error) {
          console.error('Error exporting to Excel:', error);
          this.notifyjs('Error exporting to Excel. Please ensure xlsx library is installed.', 'error');
      }
  }

  public isRowExpanded(item: any): boolean {
      return this.expandedRows.has(item);
  }

    public isNestedRowExpanded(parentItem: any, nestedItem: any): boolean {
        const nestedSet = this.nestedExpandedRows.get(parentItem);
        return nestedSet ? nestedSet.has(nestedItem) : false;
    }

  public toggleRowExpansion(item: any, event?: MouseEvent): void {
      if (event) {
          event.stopPropagation();
      }

      console.log('toggleRowExpansion', item);

      if (this.expandedRows.has(item)) {
        console.log('collapsing', item);
        // Collapsing: remove from expanded rows and clear all nested expansions
        this.expandedRows.delete(item);
        // Clear all nested row expansions for this item
        this.nestedExpandedRows.delete(item);
        // Reset level 2 pagination for this item
        this.level2PaginationOptions.delete(item);
        this.level2PaginatedData.delete(item);
      } else {
        console.log('expanding', item);
        // Expanding: add to expanded rows
        this.expandedRows.add(item);

        // Check cache first (if caching is enabled)
        if (this.cacheExpandedData && this.cachedExpandedData.has(item)) {
          // Data is already cached, no need to fetch
          console.log('data is cached', item);
          return;
        }

        // Check if data is already pre-populated (backward compatibility)
        if (item.expandedContent && item.expandedContent.length > 0) {
          console.log('data is pre-populated', item);
          // Data is pre-populated, cache it if caching is enabled
          if (this.cacheExpandedData) {
              this.cachedExpandedData.set(item, item.expandedContent);
          }
          return;
        }

        // Check if we can fetch data dynamically
        if (this.onLevel1Expand) {
          // Set loading state
          this.loadingRows.set(item, true);

          console.log('fetching data dynamically', item);

          // Call the callback function
          const result: any = this.onLevel1Expand({ item: item });
          // Handle both Promise and synchronous return values
          if (result && typeof result.then === 'function') {
              // It's a Promise
              (result as Promise<any[]>).then((data: any[]) => {
                  this.setExpandedContent(item, data || []);
                  this.loadingRows.set(item, false);
              }).catch((error: any) => {
                  console.error('Error fetching level 2 data:', error);
                  this.notifyjs('Error loading data. Please try again.', error);
                  this.loadingRows.set(item, false);
                  // Collapse the row on error
                  this.expandedRows.delete(item);
              });
          } else {
              // It's a synchronous return value
              this.setExpandedContent(item, (result as any[]) || []);
              this.loadingRows.set(item, false);
          }
        } else {
          console.log('no onLevel1Expand callback', item);
        }
      }
  }

    public toggleNestedRowExpansion(parentItem: any, nestedItem: any, event?: MouseEvent): void {
        console.log('toggleNestedRowExpansion', parentItem, nestedItem);

        if (event) {
            event.stopPropagation();
        }

        if (!this.nestedExpandedRows.has(parentItem)) {
            console.log('setting nested expanded rows', parentItem);
            this.nestedExpandedRows.set(parentItem, new Set());
        }

        const nestedSet = this.nestedExpandedRows.get(parentItem);
        console.log('nestedSet', nestedSet);
        if (nestedSet.has(nestedItem)) {
            console.log('deleting nested item', nestedItem);
            nestedSet.delete(nestedItem);
        } else {
            console.log('adding nested item', nestedItem);
            nestedSet.add(nestedItem);
        }
    }

  public getExpandIcon(item: any): string {
      return this.isRowExpanded(item) ? 'arrow_drop_up' : 'arrow_drop_down';
  }

  public getNestedExpandIcon(parentItem: any, nestedItem: any): string {
    return this.isNestedRowExpanded(parentItem, nestedItem) ? 'arrow_drop_up' : 'arrow_drop_down';
  }

    public getTotalColumnCount(): number {
        let count = 1; // Expand icon column (always present)
        if (!this.readonly) {
            count += 1; // Checkbox column
        }
        if (this.columns) {
            count += this.columns.length; // Data columns
        }
        if (!this.readonly) {
            count += 1; // Edit Actions column
        }
        if (this.showDownloadColumn) {
            count += 1; // Download Actions column
        }
        return count;
    }

    public downloadLevel1(item: any, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        if (this.onLevel1Download) {
            this.onLevel1Download({ item: item });
        }
    }

    public downloadLevel2(item: any, subItem: any, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        if (this.onLevel2Download) {
            this.onLevel2Download({ item: item, subItem: subItem });
        }
    }

    public getNestedTableIndent(): string {
        // Calculate indent to align level 3 dataGrouping column with level 2 dataGrouping column
        // Level 2 structure: expand icon (40px) + checkbox (40px if not readonly) = indent needed
        const expandIconWidth = 40;
        const checkboxWidth = this.readonly ? 0 : 40;
        const totalIndent = expandIconWidth + checkboxWidth;
        return `${totalIndent}px`;
    }

    public getLevel2DataGroupingColumnIndex(): number {
        // Level 2 structure: expand icon (index 0) + checkbox (index 1 if not readonly) + dataGrouping (next index)
        return this.readonly ? 1 : 2;
    }

    public getLevel2Columns(): TableColumn[] {
        return slice(this.columns, 1);
    }

    public getNestedColumnStyle(column: TableColumn): string {
        // For nested tables, only set width if explicitly defined
        // Don't calculate equal widths - let content determine size
        if (column.width) {
            if (column.width.includes('px') || column.width.includes('%') || column.width.includes('em')) {
                return `width: ${column.width};`;
            }
        }
        // Return empty string to let browser calculate based on content
        return '';
    }

    public isRowLoading(item: any): boolean {
        return this.loadingRows.has(item) && this.loadingRows.get(item) === true;
    }

    public getExpandedContent(item: any): any[] {
        // First check cache if caching is enabled
        if (this.cacheExpandedData && this.cachedExpandedData.has(item)) {
            return this.cachedExpandedData.get(item);
        }
        // Fall back to pre-populated expandedContent (backward compatibility)
        return item.expandedContent || [];
    }

    public getPaginatedExpandedContent(item: any): any[] {
        const expandedContent = this.getExpandedContent(item);

        if (!expandedContent || expandedContent.length === 0) {
            return [];
        }

        // Initialize pagination for this item if not exists
        if (!this.level2PaginationOptions.has(item)) {
            this.level2PaginationOptions.set(item, {
                currentPage: 1,
                itemsPerPage: this.level2ItemsPerPage,
                totalItems: expandedContent.length
            });
        }

        const paginationOpts = this.level2PaginationOptions.get(item);

        // Check if pagination is needed
        const needsPagination = expandedContent.length > paginationOpts.itemsPerPage;

        if (!needsPagination) {
            return expandedContent;
        }

        // Calculate paginated data
        const startIndex = (paginationOpts.currentPage - 1) * paginationOpts.itemsPerPage;
        const endIndex = startIndex + paginationOpts.itemsPerPage;
        const paginated = expandedContent.slice(startIndex, endIndex);

        // Update total items
        paginationOpts.totalItems = expandedContent.length;
        this.level2PaginationOptions.set(item, paginationOpts);
        this.level2PaginatedData.set(item, paginated);

        return paginated;
    }

    public getLevel2PaginationOptions(item: any): any {
        if (!this.level2PaginationOptions.has(item)) {
            const expandedContent = this.getExpandedContent(item);
            this.level2PaginationOptions.set(item, {
                currentPage: 1,
                itemsPerPage: this.level2ItemsPerPage,
                totalItems: expandedContent ? expandedContent.length : 0
            });
        }
        return this.level2PaginationOptions.get(item);
    }

    public shouldShowLevel2Pagination(item: any): boolean {
        if (!this.enableLevel2Pagination) {
            return false;
        }
        const expandedContent = this.getExpandedContent(item);
        if (!expandedContent || expandedContent.length === 0) {
            return false;
        }
        const paginationOpts = this.getLevel2PaginationOptions(item);
        return expandedContent.length > paginationOpts.itemsPerPage;
    }

    public onLevel2PageChanged(item: any): void {
        const paginationOpts = this.getLevel2PaginationOptions(item);
        this.level2PaginationOptions.set(item, paginationOpts);
        // Force update by clearing cached paginated data
        this.level2PaginatedData.delete(item);
    }

    public onLevel2ItemsPerPageChanged(item: any): void {
        const paginationOpts = this.getLevel2PaginationOptions(item);
        paginationOpts.currentPage = 1;
        this.level2PaginationOptions.set(item, paginationOpts);
        // Force update by clearing cached paginated data
        this.level2PaginatedData.delete(item);
    }

    public getLevel2RowCount(item: any): number {
        const expandedContent = this.getExpandedContent(item);
        return expandedContent ? expandedContent.length : 0;
    }

    public setExpandedContent(item: any, data: any[]): void {
        // Set expandedContent on the item
        item.expandedContent = data;
        // Cache it if caching is enabled
        if (this.cacheExpandedData) {
            this.cachedExpandedData.set(item, data);
        }
    }

    public clearCache(): void {
        this.cachedExpandedData.clear();
        this.expandedRows.clear();
        this.nestedExpandedRows.clear();
    }
}

export const ReusableNestedTableComponent = {
    template: require('./reusable-nested-table.component.html'),
    style: require('./reusable-nested-table.component.scss'),
    controller: ReusableNestedTableController,
    controllerAs: '$ctrl',
    bindings: {
        columns: '<',
        nestedColumns: '<',
        displayColumns: '<?',
        displayNestedColumnsHeader: '<?',
        data: '<',
        emptyMessage: '@?',
        loading: '<?',
        title: '@?',
        addButtonText: '@?',
        addButtonIcon: '@?',
        readonly: '<?',
        itemIdField: '@?',
        enablePagination: '<?',
        enableLevel2Pagination: '<?',
        level2ItemsPerPage: '<?',
        hideExcelButton: '<?',
        showDownloadColumn: '<?',
        alternateExpandedRowFontColors: '<?',
        autoSizeColumns: '<?',
        cacheExpandedData: '<?',

        onAdd: '&?',
        onEdit: '&?',
        onDelete: '&?',
        onPageChange: '&?',
        onRefresh: '&?',
        onRowClick: '&?',
        onLevel1Expand: '&?',
        onLevel1Download: '&?',
        onLevel2Download: '&?',
    }
};




// WEBPACK FOOTER //
// ./src/components/reusable-nested-table/reusable-nested-table.component.ts